import { ratio, type Ratio } from '@radish/primitives';
import { floorDiv, round } from './rounding.js';

/**
 * Exact emulation of IEEE-754 binary64 arithmetic, in bigint, for legacy parity only (pricing rulesVersion 1).
 *
 * The legacy register computes some money steps in JavaScript doubles (e.g. `0.009 * 1500` is 13.499999999999998,
 * so `Math.round` gives 13 where exact arithmetic gives 14). Core never uses floating point, so it reproduces those
 * steps here: a double is represented by its exact value as a Ratio, and every operation rounds the exact result to
 * the nearest binary64 (ties to even), exactly as the hardware does. Supported range: finite doubles (overflow throws
 * RangeError, as no money path reaches it); subnormals round correctly but Dinero's digit counting is only
 * specified for |x| ≥ 1e-12, far below any rate or ratio legacy uses.
 */
export type F64 = Ratio;

/** Bits in |n| (0 for 0), from its hex digits: cheaper than a base-2 string on the long numerators division makes. */
const bitLength = (n: bigint): number => {
  if (n === 0n) return 0;
  const hex = (n < 0n ? -n : n).toString(16);
  return (hex.length - 1) * 4 + (32 - Math.clz32(parseInt(hex[0] ?? '0', 16)));
};
const MIN_EXP = -1074; // smallest subnormal exponent
const MANTISSA_MAX = 1n << 53n;
const MANTISSA_MIN = 1n << 52n;

/**
 * The binary64 nearest to num/den (round half to even), for any den ≠ 0; num/den need not be reduced. The result is
 * m·2^e, so it is reduced by dropping common factors of two alone: no gcd, which the arithmetic below would otherwise
 * pay on every operation (the legacy True Margin rule runs hundreds of these per priced order).
 */
const nearest = (num: bigint, den: bigint): F64 => {
  if (den === 0n) throw new RangeError('ratio: denominator must not be zero');
  if (num === 0n) return { num: 0n, den: 1n };
  const negative = num < 0n !== den < 0n;
  const n = num < 0n ? -num : num;
  const d = den < 0n ? -den : den;
  // choose e so that the 53-bit mantissa m = n / (d · 2^e) lies in [2^52, 2^53)
  let e = bitLength(n) - bitLength(d) - 53;
  const scaled = (exp: number): [bigint, bigint] => (exp >= 0 ? [n, d << BigInt(exp)] : [n << BigInt(-exp), d]);
  for (;;) {
    const [sn, sd] = scaled(e);
    const q = sn / sd;
    if (q >= MANTISSA_MAX) e += 1;
    else if (q < MANTISSA_MIN) e -= 1;
    else break;
  }
  if (e < MIN_EXP) e = MIN_EXP; // subnormal: fixed exponent, fewer mantissa bits
  if (e > 1024 - 53) throw new RangeError('toF64: overflow (Infinity is never a legacy money value)');
  const [sn, sd] = scaled(e);
  const q = sn / sd;
  const rem = sn - q * sd;
  const up = 2n * rem > sd || (2n * rem === sd && (q & 1n) === 1n);
  let m = up ? q + 1n : q;
  if (m === 0n) return { num: 0n, den: 1n };
  let shift = e;
  if (shift < 0) {
    while ((m & 1n) === 0n && shift < 0) {
      m >>= 1n;
      shift += 1;
    }
  }
  const signed = negative ? -m : m;
  return shift >= 0 ? { num: signed << BigInt(shift), den: 1n } : { num: signed, den: 1n << BigInt(-shift) };
};

/** The binary64 nearest to an exact rational (round half to even). */
export const toF64 = (r: Ratio): F64 => nearest(r.num, r.den);

export const f64 = (n: bigint): F64 => nearest(n, 1n);
export const f64Add = (a: F64, b: F64): F64 => nearest(a.num * b.den + b.num * a.den, a.den * b.den);
export const f64Sub = (a: F64, b: F64): F64 => nearest(a.num * b.den - b.num * a.den, a.den * b.den);
export const f64Mul = (a: F64, b: F64): F64 => nearest(a.num * b.num, a.den * b.den);
export const f64Div = (a: F64, b: F64): F64 => nearest(a.num * b.den, a.den * b.num);
export const f64Compare = (a: F64, b: F64): -1 | 0 | 1 => {
  const d = a.num * b.den - b.num * a.den;
  if (d === 0n) return 0;
  return d < 0n ? -1 : 1;
};
export const f64IsInteger = (a: F64): boolean => a.den === 1n;

/** JavaScript `Math.round`: nearest integer, ties toward +∞, on the double's exact value. */
export const jsRound = (a: F64): bigint => round(a, 'HALF_UP');

/** `Number(x.toFixed(digits))`: round the exact value half up to `digits` decimals, then parse to the nearest double. */
export const jsToFixedNumber = (a: F64, digits: number): F64 => {
  const scale = 10n ** BigInt(digits);
  const negative = a.num < 0n;
  const abs = negative ? ratio(-a.num, a.den) : a;
  const k = floorDiv(2n * abs.num * scale + abs.den, 2n * abs.den); // ties: the larger n (ECMA-262 toFixed)
  return toF64(ratio(negative ? -k : k, scale));
};

/** Decimal exponent n with 10^(n−1) ≤ |a| < 10^n, for a ≠ 0. */
const decimalExponent = (a: F64): number => {
  const n = a.num < 0n ? -a.num : a.num;
  let e = n.toString().length - a.den.toString().length;
  const ge = (exp: number): boolean => (exp >= 0 ? n >= a.den * 10n ** BigInt(exp) : n * 10n ** BigInt(-exp) >= a.den);
  while (ge(e)) e += 1;
  while (!ge(e - 1)) e -= 1;
  return e;
};

/**
 * Fraction digits of `x.toString()` as Dinero's `countFractionDigits` counts them: the shortest decimal that
 * round-trips (ECMA-262 Number::toString), and for exponent notation (`1e-7`) the number after `e-`.
 *
 * The shortest k is found by bisection over 1…17: round-tripping is monotone in k, because the nearest k-digit
 * decimal is also a (k+1)-digit one, so the nearest (k+1)-digit decimal is never farther from x. Fractions stay
 * unreduced (no gcd): each probe only rounds them and compares the double they land on.
 */
export const jsFractionDigits = (a: F64): number => {
  if (a.num === 0n) return 0;
  const abs = a.num < 0n ? { num: -a.num, den: a.den } : a;
  const n0 = decimalExponent(abs);
  /** The k-significant-digit integer s (value ≈ s × 10^shift), and whether s × 10^shift reads back as x. */
  const probe = (k: number): { s: bigint; shift: number; roundTrips: boolean } => {
    const shift = n0 - k;
    const p10 = 10n ** BigInt(shift >= 0 ? shift : -shift);
    const s = round(shift >= 0 ? { num: abs.num, den: abs.den * p10 } : { num: abs.num * p10, den: abs.den }, 'HALF_UP');
    const back = shift >= 0 ? nearest(s * p10, 1n) : nearest(s, p10);
    return { s, shift, roundTrips: f64Compare(back, abs) === 0 };
  };
  let lo = 1;
  let hi = 17;
  if (!probe(hi).roundTrips) throw new RangeError('jsFractionDigits: no round-tripping decimal within 17 digits');
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (probe(mid).roundTrips) hi = mid;
    else lo = mid + 1;
  }
  const { s, shift } = probe(lo);
  // value = 0.d1…dk × 10^n once trailing zeros are dropped
  const digits = s.toString().replace(/0+$/, '');
  const n = shift + s.toString().length;
  if (n <= -6) return 1 - n; // exponent notation: '1.5e-7' → 7
  return Math.max(0, digits.length - n);
};

/**
 * Dinero.js 1.9 `multiply(multiplier)` on an integer amount before its rounding (calculator.multiply): integers
 * multiply directly; otherwise both operands are scaled by 10^(fraction digits), rounded, multiplied and divided back,
 * all in doubles. `Math.round` of this value is Dinero's HALF_UP result.
 */
export const dineroMultiply = (amount: bigint, multiplier: F64): F64 => {
  const a = f64(amount);
  if (f64IsInteger(multiplier)) return f64Mul(a, multiplier);
  const factor = f64(10n ** BigInt(Math.max(0, jsFractionDigits(multiplier))));
  const product = f64Mul(f64(jsRound(f64Mul(a, factor))), f64(jsRound(f64Mul(multiplier, factor))));
  return f64Div(product, f64Mul(factor, factor));
};

/** Dinero.js 1.9 `multiply(multiplier, 'HALF_UP')` on an integer amount: `Math.round` of `dineroMultiply`. */
export const dineroMultiplyHalfUp = (amount: bigint, multiplier: F64): bigint => jsRound(dineroMultiply(amount, multiplier));

const POWERS_OF_TEN: readonly F64[] = Array.from({ length: 31 }, (_, i) => (i <= 8 ? nearest(1n, 10n ** BigInt(8 - i)) : nearest(10n ** BigInt(i - 8), 1n)));
/** PHP `php_intpow10(power)`: the double 10^power (exact up to 10^22; C `pow` beyond, which is the nearest double). */
const phpPow10 = (power: number): F64 => (power <= 0 ? nearest(1n, 10n ** BigInt(-power)) : nearest(10n ** BigInt(power), 1n));
/** PHP `php_intlog10abs(value)`: floor(log10(|value|)), by its table of doubles 1e-8 … 1e22 (exact log beyond it). */
const phpIntLog10Abs = (a: F64): number => {
  const abs = a.num < 0n ? { num: -a.num, den: a.den } : a;
  const first = POWERS_OF_TEN[0] as F64;
  const last = POWERS_OF_TEN[30] as F64;
  if (f64Compare(abs, first) < 0 || f64Compare(abs, last) > 0) return decimalExponent(abs) - 1;
  let i = 30;
  while (i > 0 && f64Compare(abs, POWERS_OF_TEN[i] as F64) < 0) i -= 1;
  return i - 8;
};
/** PHP `php_round_helper(value, PHP_ROUND_HALF_UP)`: floor(value + 0.5) or ceil(value − 0.5), the addition in doubles. */
const phpRoundHelper = (v: F64): F64 => {
  const half = { num: 1n, den: 2n };
  if (v.num >= 0n) {
    const t = f64Add(v, half);
    return nearest(floorDiv(t.num, t.den), 1n);
  }
  const t = f64Sub(v, half);
  return nearest(-floorDiv(-t.num, t.den), 1n);
};

/**
 * PHP 8.2 `round(value, places)` and `number_format`'s rounding (`_php_math_round`, PHP_ROUND_HALF_UP), bit for bit:
 * the value is first pre-rounded to 15 significant digits when that is finer than `places`, so `round(1.005, 2)` is
 * 1.01 although the double 1.005 is just below it, then rounded half away from zero and divided back. Legacy payroll
 * (EmployeePayrollService getFormatHours / getFormatPay) runs every hour and pay through it.
 */
export const phpRound = (value: F64, places: number): F64 => {
  if (value.num === 0n) return value;
  const precisionPlaces = 14 - phpIntLog10Abs(value);
  const f1 = phpPow10(Math.abs(places));
  let tmp: F64;
  if (precisionPlaces > places && precisionPlaces - 15 < places) {
    const f2 = phpPow10(Math.abs(precisionPlaces));
    tmp = precisionPlaces >= 0 ? f64Mul(value, f2) : f64Div(value, f2);
    tmp = phpRoundHelper(tmp);
    const back = phpPow10(Math.abs(places - precisionPlaces));
    tmp = f64Div(tmp, back);
  } else {
    tmp = places >= 0 ? f64Mul(value, f1) : f64Div(value, f1);
    const abs = tmp.num < 0n ? { num: -tmp.num, den: tmp.den } : tmp;
    if (f64Compare(abs, f64(10n ** 15n)) >= 0) return value;
  }
  tmp = phpRoundHelper(tmp);
  return places > 0 ? f64Div(tmp, f1) : f64Mul(tmp, f1);
};

/**
 * PHP 8.2 `(float) number_format(value, places, '.', '')` for places ≥ 0: `_php_math_round`, then printed with
 * `%.{places}F` (zend_dtoa: the exact double, ties to even) and read back. It equals `phpRound` below 10^15; past it
 * the print can land on another double.
 */
export const phpNumberFormat = (value: F64, places: number): F64 => nearest(phpNumberFormatUnits(value, places), 10n ** BigInt(places));

/** The integer of 10^-places units `number_format` prints: `_php_math_round`, then `%.{places}F` (ties to even). */
const phpNumberFormatUnits = (value: F64, places: number): bigint => {
  const r = phpRound(value, places);
  const scale = 10n ** BigInt(places);
  const negative = r.num < 0n;
  const n = (negative ? -r.num : r.num) * scale;
  const q = n / r.den;
  const rem = n - q * r.den;
  const k = 2n * rem > r.den || (2n * rem === r.den && (q & 1n) === 1n) ? q + 1n : q;
  return negative ? -k : k;
};

/**
 * PHP 8.2 `number_format(value, places)` as text (places > 0): the default separators, "," every three digits and "."
 * before the decimals; a result that prints as zero has no minus sign (PHP 8.0+). `thousands` '' is
 * `number_format(value, places, '.', '')`, the bare number a contract can carry.
 */
export const phpNumberFormatText = (value: F64, places: number, thousands: ',' | '' = ','): string => {
  const units = phpNumberFormatUnits(value, places);
  const digits = (units < 0n ? -units : units).toString().padStart(places + 1, '0');
  const whole = digits.slice(0, -places).replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  return `${units < 0n ? '-' : ''}${whole}.${digits.slice(-places)}`;
};

/** `x.toFixed(digits)` read as an integer of 10^-digits units: the exact value rounded half up (ECMA-262: ties take the larger n). */
export const jsToFixedScaled = (a: F64, digits: number): bigint => {
  const scale = 10n ** BigInt(digits);
  const negative = a.num < 0n;
  const n = negative ? -a.num : a.num;
  const k = floorDiv(2n * n * scale + a.den, 2n * a.den);
  return negative ? -k : k;
};

/** C `fmod(x, y)` (y ≠ 0): x − trunc(x / y) · y, which IEEE-754 computes exactly. */
export const f64Fmod = (x: F64, y: F64): F64 => {
  const q = (x.num * y.den) / (x.den * y.num); // bigint division truncates toward zero
  return nearest(x.num * y.den - q * y.num * x.den, x.den * y.den);
};

/**
 * PHP 8.2's string of a float (`"{$x}"`, `precision` = 14: `%.14G`): the double's exact value rounded to 14 significant
 * digits (ties to even, as zend_gcvt rounds the exact decimal expansion), trailing zeros dropped; an integral value
 * prints without a point ("8", not "8.0"). Exponent forms (below 1e-4 or from 1e15) are not needed by any legacy text
 * this reproduces and throw. Legacy's schedule conflicts print rest and overtime hours this way ("1.1666666666667h").
 */
export const phpFloatText = (value: F64): string => {
  if (value.num === 0n) return '0';
  const negative = value.num < 0n;
  const n = negative ? -value.num : value.num;
  const d = value.den;
  let e = n.toString().length - d.toString().length; // floor(log10(n/d)) is e or e - 1
  if (n * 10n ** BigInt(Math.max(0, -e)) < d * 10n ** BigInt(Math.max(0, e))) e -= 1;
  if (e < -4 || e >= 15) throw new RangeError(`phpFloatText: ${String(e)} is outside the fixed-point range`);
  const shift = 13 - e; // 14 significant digits
  const sn = shift >= 0 ? n * 10n ** BigInt(shift) : n;
  const sd = shift >= 0 ? d : d * 10n ** BigInt(-shift);
  let q = sn / sd;
  const rem = sn - q * sd;
  if (2n * rem > sd || (2n * rem === sd && (q & 1n) === 1n)) q += 1n;
  let digits = q.toString();
  let point = e + 1; // digits before the point
  if (digits.length > 14) point += 1; // rounding carried to a new digit (9.99… → 10)
  digits = digits.slice(0, 14);
  const whole = point <= 0 ? '0' : digits.slice(0, point).padEnd(point, '0');
  const fraction = (point < 0 ? '0'.repeat(-point) + digits : digits.slice(Math.max(0, point))).replace(/0+$/, '');
  return `${negative ? '-' : ''}${whole}${fraction === '' ? '' : `.${fraction}`}`;
};

/** The double a JavaScript number holds, as an exact F64 (its shortest decimal reads back to the same double). */
export const f64OfNumber = (x: number): F64 => {
  if (!Number.isFinite(x)) throw new RangeError('f64OfNumber: not finite');
  const [mantissa = '0', exponent = '0'] = x.toExponential().split('e');
  const [whole = '0', fraction = ''] = mantissa.replace('-', '').split('.');
  const digits = BigInt(`${whole}${fraction}`) * (x < 0 ? -1n : 1n);
  const power = Number(exponent) - fraction.length;
  return power >= 0 ? nearest(digits * 10n ** BigInt(power), 1n) : nearest(digits, 10n ** BigInt(-power));
};

/**
 * What the legacy register's percent cart discount takes from a cart (`getDiscountedItemTotals`, get-discount-amount.ts):
 * in doubles, p = percent ÷ 100, the discount Math.round(L × p), each tax bucket's share Math.round(bucket × p), and the
 * taxable share cut back when the two shares add up to more than the discount. `rateScaled` is the percent in parts per
 * 10^8 (100 % = 10^8), so the register's typed percent is rateScaled ÷ 10^6. Production's fixNegativeTotalsWithDiscount is
 * off, so nothing caps the discount at the cart.
 */
export function jsPercentCartDiscountTaken(taxable: bigint, exempt: bigint, rateScaled: bigint): bigint {
  const p = f64Div(toF64(ratio(rateScaled, 1_000_000n)), f64(100n));
  const discount = jsRound(f64Mul(f64(taxable + exempt), p));
  const shares = jsRound(f64Mul(f64(taxable), p)) + jsRound(f64Mul(f64(exempt), p));
  return shares > discount ? discount : shares;
}
